<template>
  <!-- Topic Detail Modal -->
  <div
    class="modal fade show topic-detail-modal"
    :class="{ 'd-block': show }"
    v-if="show"
    @click.self="closeModal"
    style="background-color: rgba(0,0,0,0.7); z-index: 1070;"
  >
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="d-flex align-items-center flex-grow-1">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary me-3"
              @click="closeModal"
            >
              <i class="bi bi-arrow-left me-2"></i>Back to Topics
            </button>
            <div class="flex-grow-1">
              <div class="d-flex align-items-center">
                <div class="me-2">
                  <i v-if="topic?.is_sticky" class="bi bi-pin-angle-fill text-warning" title="Sticky"></i>
                  <i v-if="topic?.is_locked" class="bi bi-lock-fill text-muted" title="Locked"></i>
                  <i v-if="topic?.priority === 'high'" class="bi bi-exclamation-triangle-fill text-danger" title="High Priority"></i>
                  <i v-else-if="topic?.priority === 'medium'" class="bi bi-exclamation-triangle text-warning" title="Medium Priority"></i>
                </div>
                <h4 class="modal-title mb-0">{{ topic?.title || 'Loading...' }}</h4>
              </div>
              <div class="text-muted small" v-if="topic">
                <i class="bi bi-person me-1"></i>{{ topic.creator?.name || 'Unknown' }}
                <span class="mx-2">•</span>
                <i class="bi bi-clock me-1"></i>{{ formatDate(topic.created_at) }}
                <span class="mx-2">•</span>
                <i class="bi bi-chat me-1"></i>{{ comments.length }} comments
              </div>
            </div>
          </div>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <!-- Modal Body -->
        <div class="modal-body p-0">
          <!-- Topic Content -->
          <div class="topic-content-section p-4 border-bottom bg-light" v-if="topic">
            <div class="d-flex align-items-start">
              <img
                :src="getAvatarUrl(topic.creator)"
                alt="Author Avatar"
                class="rounded-circle me-3"
                style="width: 48px; height: 48px;"
              >
              <div class="flex-grow-1">
                <div class="topic-content bg-white p-3 rounded border">
                  <div v-html="formatContent(topic.content)"></div>
                </div>
              </div>
            </div>
          </div>
          <!-- Comments Section -->
          <div class="comments-section">
            <!-- Comments Header -->
            <div class="p-3 border-bottom bg-white">
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">
                  <i class="bi bi-chat-dots me-2"></i>
                  Comments ({{ comments.length }})
                </h6>
                <div class="d-flex gap-2">
                  <select class="form-select form-select-sm" v-model="sortOrder" @change="sortComments" style="width: auto;">
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                  </select>
                  <button
                    v-if="!topic?.is_locked"
                    class="btn btn-primary btn-sm"
                    @click="showCommentForm = true"
                  >
                    <i class="bi bi-chat me-2"></i>Reply
                  </button>
                </div>
              </div>
            </div>
            <!-- Comments Content -->
            <div class="comments-content" style="max-height: 400px; overflow-y: auto;">
              <!-- Loading State -->
              <div v-if="loading" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
              <!-- Error State -->
              <div v-else-if="error" class="alert alert-danger m-3">
                <i class="bi bi-exclamation-triangle me-2"></i>
                {{ error }}
                <button class="btn btn-sm btn-outline-danger ms-2" @click="loadComments">
                  Try Again
                </button>
              </div>
              <!-- Comment Form -->
              <div v-if="showCommentForm && !topic?.is_locked" class="comment-form-section p-3 border-bottom bg-light">
                <div class="d-flex align-items-start">
                  <img
                    :src="getAvatarUrl(currentUser)"
                    alt="Your Avatar"
                    class="rounded-circle me-3"
                    style="width: 40px; height: 40px;"
                  >
                  <div class="flex-grow-1">
                    <form @submit.prevent="saveComment">
                      <div class="mb-3">
                        <textarea
                          class="form-control"
                          rows="3"
                          v-model="commentForm.content"
                          placeholder="Write your comment..."
                          required
                          ref="commentTextarea"
                        ></textarea>
                        <small class="form-text text-muted">
                          {{ commentForm.content.length }} characters (minimum 5)
                        </small>
                      </div>
                      <div class="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          class="btn btn-secondary btn-sm"
                          @click="showCommentForm = false"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          class="btn btn-primary btn-sm"
                          :disabled="saving || commentForm.content.length < 5"
                        >
                          <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
                          Post Comment
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
              <!-- Comments List -->
              <div v-if="comments.length > 0" class="comments-list">
                <div
                  v-for="(comment, index) in comments"
                  :key="comment.id"
                  class="comment-item p-3"
                  :class="{ 'border-bottom': index < comments.length - 1 }"
                >
                  <div class="d-flex align-items-start">
                    <div class="flex-shrink-0 me-3">
                      <img
                        :src="getAvatarUrl(comment.creator)"
                        alt="User Avatar"
                        class="rounded-circle"
                        style="width: 40px; height: 40px;"
                      >
                    </div>
                    <div class="flex-grow-1">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div class="fw-semibold">{{ comment.creator?.name || 'Unknown User' }}</div>
                          <small class="text-muted">
                            <i class="bi bi-clock me-1"></i>
                            {{ formatDate(comment.created_at) }}
                            <span v-if="comment.updated_at && comment.updated_at !== comment.created_at">
                (edited)
              </span>
                          </small>
                        </div>
                        <div class="dropdown" v-if="canManageComment(comment)">
                          <button
                            class="btn btn-sm btn-ghost dropdown-toggle"
                            type="button"
                            :id="'comment-dropdown-' + comment.id"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <i class="bi bi-three-dots"></i>
                          </button>
                          <ul class="dropdown-menu dropdown-menu-end" :aria-labelledby="'comment-dropdown-' + comment.id">
                            <li v-if="canEditComment(comment)">
                              <button class="dropdown-item" @click="editComment(comment)">
                                <i class="bi bi-pencil me-2"></i>Edit
                              </button>
                            </li>
                            <li v-if="canDeleteComment(comment)">
                              <button class="dropdown-item text-danger" @click="deleteComment(comment.id)">
                                <i class="bi bi-trash me-2"></i>Delete
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div class="comment-content bg-white p-3 rounded border">
                        <div v-if="editingComment?.id === comment.id">
                          <form @submit.prevent="saveEditComment">
                            <div class="mb-2">
                <textarea
                  class="form-control form-control-sm"
                  rows="3"
                  v-model="editForm.content"
                  required
                ></textarea>
                            </div>
                            <div class="d-flex justify-content-end gap-2">
                              <button
                                type="button"
                                class="btn btn-secondary btn-sm"
                                @click="cancelEdit"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                class="btn btn-primary btn-sm"
                                :disabled="updating || editForm.content.length < 5"
                              >
                                <span v-if="updating" class="spinner-border spinner-border-sm me-2" role="status"></span>
                                Update
                              </button>
                            </div>
                          </form>
                        </div>
                        <div v-else v-html="formatContent(comment.content)"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Empty State -->
              <div v-else-if="!loading" class="text-center py-5">
                <i class="bi bi-chat-dots text-muted" style="font-size: var(--font-size-4xl);"></i>
                <h5 class="mt-3 text-muted">No comments yet</h5>
                <p class="text-muted">Be the first to share your thoughts!</p>
                <button
                  v-if="!topic?.is_locked"
                  class="btn btn-primary"
                  @click="showCommentForm = true"
                >
                  <i class="bi bi-chat me-2"></i>Add Comment
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="delete-modal-backdrop"
      @click.self="showDeleteModal = false"
    >
      <div class="delete-modal-container">
        <div class="delete-modal-content">
          <div class="text-center p-4">
            <div class="mb-3">
              <i class="bi bi-exclamation-triangle text-warning" style="font-size: var(--font-size-4xl);"></i>
            </div>
            <h5 class="mb-3 fw-bold">Delete Comment?</h5>
            <p class="text-muted mb-4">
              This action cannot be undone. The comment will be permanently removed.
            </p>
            <div class="d-flex gap-2 justify-content-center">
              <button
                type="button"
                class="btn btn-light px-4"
                @click="showDeleteModal = false"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-danger px-4"
                @click="confirmDeleteComment"
                :disabled="deleting"
              >
                <span v-if="deleting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                <i v-if="!deleting" class="bi bi-trash me-2"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Moderation Notice Popup -->
    <transition name="fade">
      <div
        v-if="showModerationNotice"
        class="moderation-popup position-fixed bottom-0 end-0 m-4 p-3 rounded shadow bg-white border"
        style="z-index: 2000; width: 320px;"
      >
        <div class="d-flex align-items-start">
          <i class="bi bi-hourglass-split text-warning me-3" style="font-size: var(--font-size-xl);"></i>
          <div>
            <h6 class="fw-bold mb-1">Comment Awaiting Approval</h6>
            <p class="mb-0 small text-muted">
              Your comment was submitted successfully and is awaiting moderator approval.
            </p>
          </div>
        </div>
      </div>
    </transition>

    <!-- Toast Notification -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
      <div class="toast" :class="{ show: toast.show, 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error' }" role="alert">
        <div class="toast-header" :class="{ 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error', 'text-white': true }">
          <i class="bi me-2" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
          <strong class="me-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
        </div>
        <div class="toast-body text-white">
          {{ toast.message }}
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import ForumService from '@/services/forums/ForumServices'
import AnalyticsService from '@/services/analytics/AnalyticsService'
import { avatarMixin } from '@/mixins/avatarMixin'

export default {
  name: 'TopicDetailModal',
  mixins: [avatarMixin],
  props: {
    show: {
      type: Boolean,
      default: false
    },
    topic: {
      type: Object,
      default: null
    },
    forum: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'topic-updated'],
  data() {
    return {
      comments: [],
      loading: false,
      saving: false,
      updating: false,
      deleting: false,
      error: null,
      showCommentForm: false,
      showDeleteModal: false,
      sortOrder: 'newest',
      editingComment: null,
      commentToDelete: null,
      commentForm: {
        content: ''
      },
      editForm: {
        content: ''
      },
      showModerationNotice: false,
      toast: {
        show: false,
        type: 'success',
        message: ''
      },
    }
  },
  computed: {
    currentUser() {
      // Replace with your actual user state from store/auth
      return this.$store?.state?.auth?.user || { name: 'Current User' };
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.topic) {
        this.loadComments();
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
      } else {
        // Restore body scroll when modal is closed
        document.body.style.overflow = '';
        this.resetModal();
      }
    },
    showCommentForm(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.$refs.commentTextarea?.focus();
        });
      }
    }
  },
  beforeUnmount() {
    // Ensure body scroll is restored if component is destroyed
    document.body.style.overflow = '';
  },
  methods: {
    showToast(type, message) {
      this.toast = {
        show: true,
        type: type,
        message: message
      }
      setTimeout(() => {
        this.toast.show = false
      }, 3000)
    },

    async loadComments() {
      if (!this.topic?.id) return;
      this.loading = true;
      this.error = null;

      try {
        const response = await ForumService.getTopicComments(this.topic.id);

        if (response && response.success) {
          // 🧩 Safely unwrap nested paginator layers
          const comments =
            response.data?.data?.data?.data || // deepest level (like your topics)
            response.data?.data?.data ||       // typical Laravel paginator
            response.data?.data ||             // simple service wrapper
            [];

          // ✅ Always ensure it's an array before sorting
          this.comments = Array.isArray(comments) ? comments : [];

          // ✅ Safe sort
          if (this.comments.length > 0) {
            this.sortComments();
          }
        } else {
          this.error = response?.message || 'Failed to load comments';
          this.comments = [];
        }
      } catch (error) {
        console.error('Load Comments Error:', error);
        this.error = error.message || 'Failed to load comments';
        this.comments = [];
      } finally {
        this.loading = false;
      }
    },
    sortComments() {
      if (!Array.isArray(this.comments)) return;
      if (this.sortOrder === 'newest') {
        this.comments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } else {
        this.comments.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      }
    },
    async saveComment() {
      if (this.commentForm.content.length < 5) return;
      this.saving = true;
      try {
        const response = await ForumService.createComment(this.topic.id, {
          content: this.commentForm.content
        });

        if (response && response.success) {
          this.commentForm.content = '';
          this.showCommentForm = false;
          AnalyticsService.trackEvent('forum_comment_create', { topic_id: this.topic.id });
          await this.loadComments();
          this.$emit('topic-updated');

          // ✅ If forum is moderated, show moderation popup
          if (this.forum?.enable_mod) {
            this.showModerationNotice = true;
            setTimeout(() => {
              this.showModerationNotice = false;
            }, 4000); // auto-hide after 4 seconds
          } else {
            this.showToast('success','Comment posted successfully!');
          }
        } else {
          this.showToast('error',response?.message || 'Failed to post comment');
        }
      } catch (error) {
        this.showToast('error',error.message || 'Failed to post comment');
      } finally {
        this.saving = false;
      }
    },
    editComment(comment) {
      this.editingComment = comment;
      this.editForm.content = comment.content;
    },
    cancelEdit() {
      this.editingComment = null;
      this.editForm.content = '';
    },
    async saveEditComment() {
      if (this.editForm.content.length < 5) return;
      this.updating = true;
      try {
        const response = await ForumService.updateComment(this.editingComment.id, {
          content: this.editForm.content
        });
        if (response && response.success) {
          this.showToast('success','Comment updated successfully!');
          this.cancelEdit();
          await this.loadComments();
          this.$emit('topic-updated');
        } else {
          this.showToast('error',response?.message || 'Failed to update comment');
        }
      } catch (error) {
        this.showToast('error',error.message || 'Failed to update comment');
      } finally {
        this.updating = false;
      }
    },
    deleteComment(commentId) {
      this.commentToDelete = commentId;
      this.showDeleteModal = true;
    },
    async confirmDeleteComment() {
      if (!this.commentToDelete) return;
      this.deleting = true;
      try {
        const response = await ForumService.deleteComment(this.commentToDelete);
        if (response && response.success) {
          this.showToast('success','Comment deleted successfully!');
          await this.loadComments();
          this.$emit('topic-updated');
        } else {
          this.showToast('error',response?.message || 'Failed to delete comment');
        }
      } catch (error) {
        this.showToast('error',error.message || 'Failed to delete comment');
      } finally {
        this.deleting = false;
        this.showDeleteModal = false;
        this.commentToDelete = null;
      }
    },
    canManageComment(comment) {
      const currentUser = this.currentUser;
      return currentUser && (
        this.canEditComment(comment) ||
        this.canDeleteComment(comment)
      );
    },
    canEditComment(comment) {
      const currentUser = this.currentUser;
      if (!currentUser) return false;

      // Users can edit their own comments
      if (currentUser.id === comment.creator?.id) return true;

      // Check if user is admin
      return this.isAdmin(currentUser);
    },
    canDeleteComment(comment) {
      const currentUser = this.currentUser;
      // Only Super Admin can delete comments
      return currentUser && this.isSuperAdmin(currentUser);
    },
    isSuperAdmin(user) {
      if (!user || !Array.isArray(user.roles)) return false;

      // Check if user has Super Admin role (id: 1 or name: 'Super Admin')
      return user.roles.some(role =>
        (typeof role === 'object' && role.id === 1) ||
        (typeof role === 'object' && role.name === 'Super Admin')
      );
    },
    isAdmin(user) {
      if (!user || !Array.isArray(user.roles)) return false;

      // Check for various admin roles
      // Based on your table: Super Admin (1), EDCTP Admin (2), CMS Admin (3)
      const adminRoleIds = [1, 2, 3];
      const adminRoleNames = ['Super Admin', 'EDCTP Admin', 'CMS Admin'];

      return user.roles.some(role =>
        (typeof role === 'object' && adminRoleIds.includes(role.id)) ||
        (typeof role === 'object' && adminRoleNames.includes(role.name))
      );
    },
    closeModal() {
      this.$emit('close');
    },
    resetModal() {
      this.showCommentForm = false;
      this.showDeleteModal = false;
      this.editingComment = null;
      this.commentToDelete = null;
      this.commentForm.content = '';
      this.editForm.content = '';
      this.error = null;
    },
    formatContent(content) {
      if (!content) return '';
      // Check if content is a JSON string and parse it
      if (typeof content === 'string' && content.startsWith('{')) {
        try {
          const parsed = JSON.parse(content);
          // If it's an object with a content property, use that
          if (parsed.content) {
            content = parsed.content;
          } else if (typeof parsed === 'object') {
            // If it's just an object, convert to readable format
            return Object.entries(parsed)
              .map(([key, value]) => `${key}: ${value}`)
              .join('<br>');
          }
        } catch (e) {
          // If parsing fails, use content as is
          console.warn('Failed to parse content as JSON:', e);
        }
      }
      // Convert line breaks to <br> tags for proper display
      return content.replace(/\n/g, '<br>');
    },
    getAvatarUrl(user) {
      return this.getUserAvatar(user);
    },
    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays === 1) {
        return 'Today at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays === 2) {
        return 'Yesterday at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays <= 7) {
        return diffDays + ' days ago';
      } else {
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
    }
  }
}
</script>

<style scoped>
.topic-detail-modal .modal-dialog {
  max-width: 90vw;
  margin: 1rem auto;
}
.topic-detail-modal .modal-content {
  height: 90vh;
  display: flex;
  flex-direction: column;
}
.topic-detail-modal .modal-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.comments-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.comments-content {
  flex: 1;
  overflow-y: auto;
}
.topic-content-section {
  flex-shrink: 0;
}
.topic-content {
  font-size: var(--font-size-base);
  line-height: var(--line-height-relaxed);
}
.comment-item:last-child {
  border-bottom: none !important;
}
.comment-content {
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
}
.btn-ghost {
  background: none;
  border: none;
  color: #6c757d;
  padding: 0.25rem 0.5rem;
}
.btn-ghost:hover {
  background-color: #f8f9fa;
  color: #495057;
}
.modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
  flex-shrink: 0;
}
.modal-title {
  color: #2c3e50;
  font-weight: var(--font-weight-semibold);
}
.btn-close {
  background: none;
  border: none;
  font-size: var(--font-size-lg);
  opacity: 0.6;
}
.btn-close:hover {
  opacity: 1;
}
.dropdown-toggle::after {
  display: none;
}
.dropdown-item {
  padding: 0.5rem 1rem;
  font-size: var(--font-size-sm);
}
.dropdown-item:hover {
  background-color: #f8f9fa;
}
.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
  color: #721c24;
}
/* Scrollbar styling */
.comments-content::-webkit-scrollbar {
  width: 6px;
}
.comments-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.comments-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}
.comments-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
/* Delete Modal Styling */
.delete-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 1080;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-modal-container {
  max-width: 400px;
  width: 90%;
  margin: 20px;
}

.delete-modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
}

.delete-modal-content .btn {
  border-radius: 8px;
  font-weight: var(--font-weight-medium);
  transition: all 0.2s ease;
}

.delete-modal-content .btn-light {
  background-color: #f8f9fa;
  border-color: #dee2e6;
  color: #6c757d;
}

.delete-modal-content .btn-light:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
  transform: translateY(-1px);
}

.delete-modal-content .btn-danger:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
}
/* Moderation popup fade animation */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.4s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.moderation-popup {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

</style>
